import React from "react";
import { useShow } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Card, Tag, Table, Alert, Statistic, Row, Col, Space, Spin } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import { 
    FileTextOutlined,
    EditOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    StopOutlined,
    CalculatorOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../../components/ui";
import { 
    IJournalEntry,
    getJournalEntryStatusLabel, 
    getTransactionSourceLabel,
    JournalEntryStatus,
    TransactionSource 
} from "../../../interfaces/accounting";

export const JournalEntriesShow: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { queryResult } = useShow({
        resource: "journalentries",
        id,
    });
    const { data, isLoading } = queryResult;
    const record: IJournalEntry = data?.data;

    const getStatusColor = (status: JournalEntryStatus): string => {
        switch (status) {
            case JournalEntryStatus.Draft: return "orange";
            case JournalEntryStatus.Posted: return "green";
            case JournalEntryStatus.Cancelled: return "red";
            default: return "default";
        }
    };

    const getStatusIcon = (status: JournalEntryStatus) => {
        switch (status) {
            case JournalEntryStatus.Draft: return <ClockCircleOutlined />;
            case JournalEntryStatus.Posted: return <CheckCircleOutlined />;
            case JournalEntryStatus.Cancelled: return <StopOutlined />;
            default: return null;
        }
    };

    const getSourceColor = (source: TransactionSource): string => {
        switch (source) {
            case TransactionSource.Manual: return "blue";
            case TransactionSource.Sales: return "green";
            case TransactionSource.Purchase: return "orange";
            case TransactionSource.Inventory: return "purple";
            case TransactionSource.Payroll: return "cyan";
            case TransactionSource.BankTransfer: return "geekblue";
            case TransactionSource.Adjustment: return "magenta";
            case TransactionSource.YearEndClose: return "red";
            default: return "default";
        }
    };

    const isBalanced = Math.abs((record?.totalDebits || 0) - (record?.totalCredits || 0)) < 0.01;

    if (isLoading) {
        return (
            <div className="page-container flex items-center justify-center" style={{ minHeight: 400 }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="page-container">
            <PageHeader
                title={record?.journalNumber || "Journal Entry"}
                titleIcon={<FileTextOutlined />}
                breadcrumbs={[
                    { title: "Accounting", href: "/accounting" },
                    { title: "Journal Entries", href: "/journalentries" },
                    { title: record?.journalNumber || "Details" },
                ]}
                actions={[
                    {
                        label: "Edit",
                        onClick: () => navigate(`/journalentries/edit/${id}`),
                        type: "primary",
                        icon: <EditOutlined />,
                    },
                ]}
            >
                {/* Mini Stat Cards */}
                <div className="mini-stat-cards-row">
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Status</div>
                        <Tag 
                            color={getStatusColor(record?.status)} 
                            icon={getStatusIcon(record?.status)}
                        >
                            {getJournalEntryStatusLabel(record?.status)}
                        </Tag>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Total Debits</div>
                        <div className="mini-stat-value text-success">${(record?.totalDebits || 0).toFixed(2)}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Total Credits</div>
                        <div className="mini-stat-value text-danger">${(record?.totalCredits || 0).toFixed(2)}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Source</div>
                        <Tag color={getSourceColor(record?.source)}>
                            {getTransactionSourceLabel(record?.source)}
                        </Tag>
                    </div>
                </div>
            </PageHeader>

            <div className="space-y-4">
                {/* Entry Information Card */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Entry Information</h3>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Journal Number:</span>
                                <span className="info-value font-semibold">{record?.journalNumber}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Description:</span>
                                <span className="info-value">{record?.description}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Reference:</span>
                                <span className="info-value">{record?.reference || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Source:</span>
                                <Tag color={getSourceColor(record?.source)}>
                                    {getTransactionSourceLabel(record?.source)}
                                </Tag>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Transaction Date:</span>
                                <span className="info-value">
                                    <DateField value={record?.transactionDate} format="YYYY-MM-DD" />
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Posting Date:</span>
                                <span className="info-value">
                                    {record?.postingDate ? (
                                        <DateField value={record.postingDate} format="YYYY-MM-DD" />
                                    ) : "-"}
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Created By:</span>
                                <Tag color="blue">{record?.createdBy}</Tag>
                            </div>
                            {record?.postedBy && (
                                <div className="info-item">
                                    <span className="info-label">Posted By:</span>
                                    <Tag color="green">{record.postedBy}</Tag>
                                </div>
                            )}
                            {record?.sourceDocumentType && (
                                <div className="info-item">
                                    <span className="info-label">Source Document:</span>
                                    <Tag color="purple">
                                        {record.sourceDocumentType}
                                        {record.sourceDocumentId && ` #${record.sourceDocumentId}`}
                                    </Tag>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {record?.notes && (
                    <Alert
                        message="Notes"
                        description={record.notes}
                        type="info"
                        showIcon
                    />
                )}

                {/* Balance Summary */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Balance Summary</h3>
                    </div>
                    <div className="info-card-body">
                        <Row gutter={16}>
                            <Col xs={24} sm={8}>
                                <Card size="small" className="text-center">
                                    <Statistic
                                        title="Total Debits"
                                        value={record?.totalDebits || 0}
                                        precision={2}
                                        prefix="$"
                                        valueStyle={{ fontSize: '18px', color: 'var(--color-success)' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={8}>
                                <Card size="small" className="text-center">
                                    <Statistic
                                        title="Total Credits"
                                        value={record?.totalCredits || 0}
                                        precision={2}
                                        prefix="$"
                                        valueStyle={{ fontSize: '18px', color: 'var(--color-danger)' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={8}>
                                <Card size="small" className="text-center">
                                    <Statistic
                                        title="Difference"
                                        value={(record?.totalDebits || 0) - (record?.totalCredits || 0)}
                                        precision={2}
                                        prefix="$"
                                        valueStyle={{ 
                                            fontSize: '18px',
                                            color: isBalanced ? 'var(--color-success)' : 'var(--color-danger)'
                                        }}
                                    />
                                </Card>
                            </Col>
                        </Row>
                        
                        {!isBalanced && (
                            <Alert
                                message="Balance Warning"
                                description="This journal entry is not balanced. Debits do not equal credits."
                                type="error"
                                showIcon
                                style={{ marginTop: 16 }}
                            />
                        )}
                    </div>
                </div>

                {/* Ledger Entries */}
                <Card 
                    title={
                        <Space>
                            <CalculatorOutlined />
                            Ledger Entries ({record?.ledgerEntries?.length || 0})
                        </Space>
                    }
                    className="data-table-card"
                >
                    <Table
                        dataSource={record?.ledgerEntries || []}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        className="custom-table"
                        scroll={{ x: 600 }}
                    >
                        <Table.Column
                            title="Account"
                            dataIndex="accountCode"
                            width={120}
                            render={(accountCode: string) => (
                                <Tag color="blue" style={{ fontFamily: 'monospace' }}>
                                    {accountCode}
                                </Tag>
                            )}
                        />
                        <Table.Column
                            title="Description"
                            dataIndex="description"
                            ellipsis
                        />
                        <Table.Column
                            title="Debit"
                            dataIndex="debitAmount"
                            width={100}
                            align="right"
                            render={(amount: number) => (
                                <span style={{ 
                                    color: amount > 0 ? 'var(--color-success)' : 'var(--text-muted)',
                                    fontWeight: amount > 0 ? 'bold' : 'normal',
                                    fontFamily: 'monospace'
                                }}>
                                    {amount > 0 ? `$${amount.toFixed(2)}` : '-'}
                                </span>
                            )}
                        />
                        <Table.Column
                            title="Credit"
                            dataIndex="creditAmount"
                            width={100}
                            align="right"
                            render={(amount: number) => (
                                <span style={{ 
                                    color: amount > 0 ? 'var(--color-danger)' : 'var(--text-muted)',
                                    fontWeight: amount > 0 ? 'bold' : 'normal',
                                    fontFamily: 'monospace'
                                }}>
                                    {amount > 0 ? `$${amount.toFixed(2)}` : '-'}
                                </span>
                            )}
                        />
                        <Table.Column
                            title="Reference"
                            dataIndex="reference"
                            width={100}
                            responsive={['md']}
                            render={(reference: string) => 
                                reference ? (
                                    <Tag color="geekblue" style={{ fontSize: '12px' }}>
                                        {reference}
                                    </Tag>
                                ) : '-'
                            }
                        />
                    </Table>
                </Card>

                {/* Reversal Information */}
                {record?.isReversed && record?.reversalJournalId && (
                    <Alert
                        message="Reversed Entry"
                        description={
                            <Space direction="vertical">
                                <span>This journal entry has been reversed.</span>
                                <span>Reversal Journal ID: <Tag color="red">#{record.reversalJournalId}</Tag></span>
                            </Space>
                        }
                        type="warning"
                        showIcon
                    />
                )}

                {/* Audit Information */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Audit Information</h3>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Created Date:</span>
                                <span className="info-value">
                                    <DateField value={record?.createdDate} format="YYYY-MM-DD HH:mm:ss" />
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Created By:</span>
                                <Tag color="blue">{record?.createdBy}</Tag>
                            </div>
                            {record?.postedDate && (
                                <>
                                    <div className="info-item">
                                        <span className="info-label">Posted Date:</span>
                                        <span className="info-value">
                                            <DateField value={record.postedDate} format="YYYY-MM-DD HH:mm:ss" />
                                        </span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Posted By:</span>
                                        <Tag color="green">{record.postedBy}</Tag>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
